import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Edit2, Minus, Plus, X } from 'lucide-react'
import DynamicHeadphones from '@/components/customColours/DynamicHeadphones'
import CustomColoursContainer from '@/components/CustomColoursContainer'
import EngravingInput from '@/components/EngravingInput'
import useBasketStore, { IBasketItem } from '@/context/BasketStore'
import { formatPriceWithSymbol } from '@/utils/priceFormatting'
import { ICustomComponent } from '@/context/ProductDataStore'
import { getLegacyThumbnailPath, getProductThumbnailUrl } from '@/utils/productImages'
import { getProductDisplayName } from '@/utils/productDisplay'

type BasketItemRowProps = {
	item: IBasketItem
	currencySymbol?: string
	formatPrice?: (amount: number, convertToCents: boolean) => string
	isAddonFree: (productId: string, catalogueId: string) => boolean
	freeAddonCtaLabel?: string | null
	freeAddonInputMaxLength?: number
	onAddFreeAddon?: (itemId: string, inputValue?: string) => void
	isUnavailable?: boolean
	showQuantityStepper?: boolean
	onRemove: (itemId: string) => void
}

const MAX_BASKET_ITEM_QUANTITY = 99

type EngravingEditState = {
	basketItemId: string
	addonId: string
	currentText: string
	maxLength: number
}

const CUSTOM_COLOURS_ID = '674b4a1f197065b8a54a3224'

const BasketItemRow: React.FC<BasketItemRowProps> = ({
	item,
	currencySymbol,
	formatPrice,
	isAddonFree,
	freeAddonCtaLabel,
	freeAddonInputMaxLength,
	onAddFreeAddon,
	isUnavailable = false,
	showQuantityStepper = true,
	onRemove
}) => {
	const { updateBasketItem, setItemQuantity } = useBasketStore()
	const [customColoursOpen, setCustomColoursOpen] = useState<boolean>(false)
	const [activeCustomItem, setActiveCustomItem] = useState<{
		id: string
		product: IBasketItem['colourWay']
		components: ICustomComponent[]
	} | null>(null)
	const [editingEngraving, setEditingEngraving] = useState<EngravingEditState | null>(null)
	const [showFreeAddonConfirm, setShowFreeAddonConfirm] = useState(false)
	const [freeAddonInputValue, setFreeAddonInputValue] = useState('')

	useEffect(() => {
		if (!showFreeAddonConfirm) return
		const originalStyle = window.getComputedStyle(document.body).overflow
		document.body.style.overflow = 'hidden'
		return () => {
			document.body.style.overflow = originalStyle
		}
	}, [showFreeAddonConfirm])

	const handleEditCustomColours = () => {
		const components = item.colourWay.components
			? JSON.parse(JSON.stringify(item.colourWay.components))
			: []
		setActiveCustomItem({
			id: item.id,
			product: item.colourWay,
			components
		})
		setCustomColoursOpen(true)
	}

	const handleCustomColourClose = () => {
		setCustomColoursOpen(false)
		setActiveCustomItem(null)
	}

	const handleCustomColourComplete = () => {
		if (activeCustomItem) {
			updateBasketItem(activeCustomItem.id, {
				colourWay: {
					...activeCustomItem.product,
					components: activeCustomItem.components
				}
			})
		}
		setCustomColoursOpen(false)
		setActiveCustomItem(null)
	}

	const updateCustomComponent = (componentName: string, materialId: string, position: string | null) => {
		if (!activeCustomItem) return

		const updatedComponents = activeCustomItem.components.map(comp => {
			if (comp.name === componentName && comp.position === position) {
				return {
					...comp,
					chosenMaterial: { $oid: materialId }
				}
			}
			return comp
		})

		setActiveCustomItem(prev => (prev ? { ...prev, components: updatedComponents } : null))
	}

	const handleUpdateEngraving = (basketItemId: string, addonId: string, newText: string) => {
		const updatedExtrasData = item.extrasData.map(data =>
			data.itemId === addonId ? { ...data, inputValue: newText } : data
		)
		updateBasketItem(basketItemId, { extrasData: updatedExtrasData })
		setEditingEngraving(null)
	}

	const quantity = item.quantity ?? 1
	const canShowStepper = showQuantityStepper && !isUnavailable

	const handleQuantityChange = (nextQuantity: number) => {
		const clamped = Math.min(MAX_BASKET_ITEM_QUANTITY, Math.max(1, nextQuantity))
		if (clamped !== quantity) {
			setItemQuantity(item.id, clamped)
		}
	}

	const unitPrice = (() => {
		let total = item.colourWay.price?.unit_amount ?? 0
		item.extras.forEach(addon => {
			if (!isAddonFree(addon._id, addon.productCatalogueId)) {
				total += addon.price?.unit_amount ?? 0
			}
		})
		return total
	})()
	const itemPrice = unitPrice * quantity
	const heroImage = (item.colourWay as { heroImage?: { url?: string; backgroundUrl?: string } | null })
		.heroImage
	const heroForegroundUrl = typeof heroImage?.url === 'string' ? heroImage.url : null
	const heroBackgroundUrl = typeof heroImage?.backgroundUrl === 'string' ? heroImage.backgroundUrl : null
	const thumbnailUrl =
		getProductThumbnailUrl(item.colourWay) || getLegacyThumbnailPath(item.colourWay._id)

	const formattedPrice = formatPrice
		? formatPrice(itemPrice, false)
		: formatPriceWithSymbol(currencySymbol ?? '', itemPrice)

	return (
		<>
			<div
				className="basket-item flex justify-between items-center border border-black/15 p-3 pr-4 rounded-lg"
			>
				<div className={`flex items-center gap-4 ${isUnavailable ? 'opacity-50 grayscale' : ''}`}>
					<div className="rounded-md overflow-hidden relative">
						{item.colourWay._id === CUSTOM_COLOURS_ID && item.colourWay.components ? (
							<div className="relative size-[90px] md:size-[100px] cursor-pointer overflow-hidden" onClick={handleEditCustomColours}>
								<div className="absolute inset-0">
									<img
										src={`/assets/images/product_images/${item.colourWay._id}_background.webp`}
										className="w-full h-full object-cover"
										alt="Background"
									/>
								</div>
								<div className="absolute inset-0 flex items-center justify-center scale-[0.9]">
									<div className="w-full h-full relative">
										<DynamicHeadphones
											viewName="front_view"
											engraving=""
											customColourProduct={item.colourWay}
											customComponents={item.colourWay.components}
											toggleColourPicker={() => { }}
											interactive={false}
											miniature={true}
											key={JSON.stringify(item.colourWay.components)}
											className="transform-none"
										/>
									</div>
								</div>
								<div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center">
									<span className="text-white opacity-0 hover:opacity-100 transition-opacity text-sm font-medium">Edit Colours</span>
								</div>
							</div>
						) : (
							heroBackgroundUrl ? (
								<div className="relative size-[90px] md:size-[100px] overflow-hidden">
									<img
										src={heroBackgroundUrl}
										className="w-full h-full object-cover"
										alt="Background"
									/>
									<img
										src={heroForegroundUrl || thumbnailUrl}
										className="absolute inset-0 w-full h-full object-contain"
										alt={item.name}
									/>
								</div>
							) : (
								<img
									src={thumbnailUrl}
									width={100}
									className="aspect-square object-cover"
									alt={item.name}
								/>
							)
						)}
					</div>
					<div className="flex flex-col gap-1">
						<div className="font-bold leading-none">
							{item.name}
							{item.colourWay.stockLimits?.stockStatus === 'PREORDER' && (
								<span className="ml-2 hidden md:inline-flex rounded-full bg-specialYellow px-2 py-0.5 text-xs font-semibold text-black">
									Preorder
								</span>
							)}
							{item.colourWay.stockLimits?.stockStatus === 'LOW_STOCK' && !isUnavailable && (
								<span className="ml-2 inline-flex rounded-full bg-orange/15 px-2 py-0.5 text-xs font-semibold text-orange">
									{typeof item.colourWay.stockLimits.remainingStock === 'number'
										? `Only ${item.colourWay.stockLimits.remainingStock} left`
										: 'Low stock'}
								</span>
							)}
							{isUnavailable && (
								<span className="ml-2 hidden md:inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
									Unavailable
								</span>
							)}
						</div>
						<div className="opacity-70 leading-none text-sm md:text-base">{item.colourWay.optionTitle}</div>
						{item.extras.length > 0 && (
							<div className="opacity-70 leading-none text-xs flex flex-col gap-1">
						{item.extras.map((addon) => {
							const engravingInput = item.extrasData.find(data => data.itemId === addon._id)
							const isEngravingAddon = addon.componentModifications?.engravingText
							const engravingMaxLength = addon.componentModifications?.engravingText?.maxLength ?? 14
							const addonName = getProductDisplayName(addon)

							return (
								<div key={addon._id} className="flex items-center gap-1">
									<span>+ {addonName}</span>
											{isAddonFree(addon._id, addon.productCatalogueId) && (
												<span className="bg-green text-white px-1 py-0.5 rounded text-[0.75em] mt-0.25 font-semibold">FREE</span>
											)}
											{isEngravingAddon && (
												<>
													<span className="text-black/50 ml-1">
														({engravingInput?.inputValue || 'No text'})
													</span>
													<button
														onClick={() => setEditingEngraving({
															basketItemId: item.id,
															addonId: addon._id,
															currentText: engravingInput?.inputValue || '',
															maxLength: engravingMaxLength
														})}
														className="ml-1 p-1 hover:bg-gray-100 rounded-full transition-colors"
													>
														<Edit2 size={12} className="text-gray-500" />
													</button>
												</>
											)}
										</div>
									)
								})}
							</div>
						)}
						<div className="opacity-70 leading-none font-headline text-kibuBlue">
							{formattedPrice}
						</div>
						{canShowStepper && (
							<div className="mt-1 flex items-center gap-2">
								<div className="flex items-center rounded-md border border-black/15">
									<button
										type="button"
										aria-label="Decrease quantity"
										onClick={() => handleQuantityChange(quantity - 1)}
										disabled={quantity <= 1}
										className="flex h-7 w-7 items-center justify-center rounded-l-md text-black transition-colors hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-30"
									>
										<Minus size={14} />
									</button>
									<span className="min-w-[2rem] select-none text-center text-sm font-semibold tabular-nums">
										{quantity}
									</span>
									<button
										type="button"
										aria-label="Increase quantity"
										onClick={() => handleQuantityChange(quantity + 1)}
										disabled={quantity >= MAX_BASKET_ITEM_QUANTITY}
										className="flex h-7 w-7 items-center justify-center rounded-r-md text-black transition-colors hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-30"
									>
										<Plus size={14} />
									</button>
								</div>
							</div>
						)}
						{!isUnavailable && freeAddonCtaLabel && onAddFreeAddon && (
							<button
								type="button"
								onClick={() => {
									setFreeAddonInputValue('')
									setShowFreeAddonConfirm(true)
								}}
								className="mt-1 self-start rounded-md border border-green px-2 py-1 text-xs font-semibold text-green hover:bg-green/10 transition-colors"
							>
								{freeAddonCtaLabel}
							</button>
						)}
						{/* {(item.colourWay.stockLimits?.stockStatus === 'PREORDER' || isUnavailable) && (
							<div className="md:hidden flex flex-wrap -ml-1">
								{item.colourWay.stockLimits?.stockStatus === 'PREORDER' && (
									<span className="inline-flex rounded-full bg-specialYellow px-2 py-0.5 text-xs font-semibold text-black">
										Preorder
									</span>
								)}
								{isUnavailable && (
									<span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
										Unavailable
									</span>
								)}
							</div>
						)} */}
					</div>
				</div>
				<button
					className="text-red-500 bg-white border border-2 border-red-500 rounded-md p-1 transition-all ease-hypereaseout duration-[2000ms] scale-100 hover:scale-110 hover:bg-red-500 hover:text-white"
					onClick={() => onRemove(item.id)}
				>
					<X className="h-4 w-4" />
				</button>
			</div>

			{activeCustomItem && (
				<CustomColoursContainer
					open={customColoursOpen}
					customColourProduct={activeCustomItem.product}
					customComponents={activeCustomItem.components}
					updateCustomComponent={updateCustomComponent}
					onClose={handleCustomColourClose}
					onComplete={handleCustomColourComplete}
					basketItemId={activeCustomItem.id}
				/>
			)}

			{editingEngraving && (
				<>
					<div
						className="fixed inset-0 bg-black/50 z-[1001]"
						onClick={() => setEditingEngraving(null)}
					/>
					<div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl z-[1002] p-6 w-[90%] max-w-md">
						<h3 className="text-lg font-bold mb-4">Edit Engraving</h3>
						<EngravingInput
							value={editingEngraving.currentText}
							onChange={(value) => setEditingEngraving({
								...editingEngraving,
								currentText: value
							})}
							maxLength={editingEngraving.maxLength}
							className="w-full border border-gray-300 rounded-lg px-4 py-2"
							autoFocus={true}
						/>
						<div className="flex justify-end gap-2 mt-4">
							<button
								onClick={() => setEditingEngraving(null)}
								className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
							>
								Cancel
							</button>
							<button
								onClick={() => handleUpdateEngraving(
									editingEngraving.basketItemId,
									editingEngraving.addonId,
									editingEngraving.currentText
								)}
								className="px-4 py-2 text-sm bg-orange text-white rounded-lg hover:bg-orange/90"
							>
								Save
							</button>
						</div>
					</div>
				</>
			)}

			{showFreeAddonConfirm && createPortal(
				<div
					className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4"
					onClick={(event) => {
						if (event.target === event.currentTarget) {
							setShowFreeAddonConfirm(false)
						}
					}}
				>
					<div
						className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
						onClick={(event) => event.stopPropagation()}
					>
						<h3 className="text-xl font-bold mb-2">Add free addon?</h3>
						<p className="text-sm text-black/70 mb-5">
							{freeAddonCtaLabel ? `${freeAddonCtaLabel} to this item before checkout.` : 'Add this free addon to your item.'}
						</p>
						{typeof freeAddonInputMaxLength === 'number' && (
							<div className="mb-5">
								<p className="text-sm font-medium text-black mb-2">Engraving text</p>
								<EngravingInput
									value={freeAddonInputValue}
									onChange={setFreeAddonInputValue}
									maxLength={freeAddonInputMaxLength}
									className="w-full border border-gray-300 rounded-lg px-4 py-2"
									placeholder="Enter your engraving text"
									autoFocus
								/>
							</div>
						)}
						<div className="flex justify-end gap-2">
							<button
								type="button"
								onClick={() => setShowFreeAddonConfirm(false)}
								className="px-4 py-2 text-sm rounded-lg border border-black/20 hover:bg-black/5"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={() => {
									onAddFreeAddon?.(item.id, freeAddonInputValue)
									setShowFreeAddonConfirm(false)
								}}
								className="px-4 py-2 text-sm rounded-lg bg-black text-white hover:bg-black/85"
							>
								Add
							</button>
						</div>
					</div>
				</div>,
				document.body
			)}
		</>
	)
}

export default BasketItemRow
